import { assertFinite, FormatInputError, numberFormat, unsigned0 } from "./errors";

// Money display only. The currency always comes from the tariff, session or
// invoice being shown and is never derived from the locale; nothing here
// converts currencies or changes stored amounts.
//
// Two entry points match the two wire conventions (LOC-104 section 2):
// - formatMoneyMinor: integer ISO 4217 minor units. The number of minor digits
//   comes from ISO 4217 data in Intl (USD 2, JPY 0, KWD 3), so a minor amount
//   is not assumed to be cents. The billing `*_cents` fields are fixed
//   hundredths instead (moneyHundredths in ./display).
// - formatMoneyMajor: major units (`total_cost`, `rate_base`, revenue totals).

export interface MoneyFormatOptions {
  /** "symbol" ($1,234.50, default) or "code" (USD 1,234.50). */
  readonly display?: "symbol" | "code";
  /** Major-unit rates may carry more digits than the currency (0.125 per kWh). */
  readonly maximumFractionDigits?: number;
}

const CURRENCY = /^[A-Z]{3}$/;

/** Validates an ISO 4217 code that Intl knows; throws FormatInputError otherwise. */
export function assertCurrency(currency: string): void {
  if (typeof currency !== "string" || !CURRENCY.test(currency)) throw new FormatInputError("currency", String(currency));
  const supported = (Intl as { supportedValuesOf?: (key: "currency") => string[] }).supportedValuesOf?.("currency");
  if (supported && !supported.includes(currency)) throw new FormatInputError("currency", `${currency} is not an ISO 4217 currency`);
}

/** Minor-unit digits of a currency (ISO 4217 exponent): USD 2, JPY 0, KWD 3. */
export function currencyMinorDigits(currency: string): number {
  assertCurrency(currency);
  return numberFormat({ style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
}

/** Exact decimal string of an integer minor amount: (-12345, 2) -> "-123.45". */
export function minorToDecimalString(amountMinor: number | bigint, digits: number): string {
  let minor: bigint;
  if (typeof amountMinor === "bigint") minor = amountMinor;
  else {
    assertFinite(amountMinor, "amount");
    if (!Number.isInteger(amountMinor)) throw new FormatInputError("not_integer", `minor amount ${amountMinor} has a fraction`);
    if (!Number.isSafeInteger(amountMinor)) throw new FormatInputError("unsafe_integer", `minor amount ${amountMinor} is not exact; pass a bigint`);
    minor = BigInt(amountMinor);
  }
  const negative = minor < 0n;
  const abs = (negative ? -minor : minor).toString().padStart(digits + 1, "0");
  const whole = digits ? abs.slice(0, -digits) : abs;
  const fraction = digits ? `.${abs.slice(-digits)}` : "";
  return `${negative && minor !== 0n ? "-" : ""}${whole}${fraction}`;
}

function currencyFormat(currency: string, digits: number, maxDigits: number, display: "symbol" | "code"): Intl.NumberFormat {
  return numberFormat({ style: "currency", currency, currencyDisplay: display, minimumFractionDigits: digits, maximumFractionDigits: maxDigits, signDisplay: "negative" });
}

/**
 * Formats a decimal string exactly. Intl.NumberFormat accepts decimal
 * strings without going through a binary float (ES2023); the cast keeps the
 * call valid for ES2022 type libraries.
 */
function formatDecimal(format: Intl.NumberFormat, decimal: string): string {
  return (format.format as unknown as (value: string) => string)(decimal);
}

/** Integer ISO 4217 minor units: formatMoneyMinor(123450, "USD") -> "$1,234.50", formatMoneyMinor(1234, "JPY") -> "¥1,234". */
export function formatMoneyMinor(amountMinor: number | bigint, currency: string, options: MoneyFormatOptions = {}): string {
  const digits = currencyMinorDigits(currency);
  const decimal = minorToDecimalString(amountMinor, digits);
  return formatDecimal(currencyFormat(currency, digits, digits, options.display ?? "symbol"), decimal);
}

const DECIMAL = /^-?(0|[1-9][0-9]*)(\.[0-9]+)?$/;

/**
 * Major units, e.g. costs.total_cost or a rate: formatMoneyMajor(12.5, "USD") -> "$12.50".
 * Rounds half away from zero to the currency digits unless maximumFractionDigits allows more.
 */
export function formatMoneyMajor(amountMajor: number | string, currency: string, options: MoneyFormatOptions = {}): string {
  const digits = currencyMinorDigits(currency);
  const maxDigits = Math.max(digits, options.maximumFractionDigits ?? digits);
  const format = currencyFormat(currency, digits, maxDigits, options.display ?? "symbol");
  if (typeof amountMajor === "string") {
    if (!DECIMAL.test(amountMajor)) throw new FormatInputError("decimal", `major amount ${JSON.stringify(amountMajor)}`);
    return formatDecimal(format, /^-0(\.0+)?$/.test(amountMajor) ? amountMajor.slice(1) : amountMajor);
  }
  assertFinite(amountMajor, "amount");
  return format.format(unsigned0(amountMajor));
}
